import styled from "styled-components"
import { User, Users, X, Lock, Zap } from "lucide-react"

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;

  @media (max-width: 900px) {
    padding: 0;
    align-items: stretch;
    justify-content: stretch;
  }
`

const Modal = styled.div`
  background: white;
  width: 100%;
  max-width: 720px;
  border-radius: 14px;
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.22);
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    max-width: 100vw;
    width: 100vw;
    height: 100vh;
    max-height: 100vh;
    border-radius: 0;
    overflow-y: auto;
  }
`

const Header = styled.div`
  padding: 20px 24px 8px;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`

const HeadText = styled.div`
  h2 {
    margin: 0 0 4px 0;
    font-size: 20px;
    font-weight: 700;
    color: #0f172a;
  }
  p {
    margin: 0;
    font-size: 13px;
    color: #64748b;
  }
`

const CloseBtn = styled.button`
  border: none;
  background: transparent;
  cursor: pointer;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: #64748b;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  &:hover { background: #f1f5f9; color: #0f172a; }
`

const Body = styled.div`
  padding: 18px 24px 28px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
  }
`

const Card = styled.button`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 22px 20px;
  border: 2px solid ${(p) => (p.accent || "#e5e7eb")};
  background: ${(p) => p.bg || "white"};
  border-radius: 12px;
  cursor: pointer;
  text-align: left;
  transition: transform 0.06s, box-shadow 0.15s, border-color 0.15s;

  &:hover {
    border-color: ${(p) => (p.accentHover || "#2563eb")};
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
    transform: translateY(-1px);
  }
  &:active { transform: translateY(0); }
`

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`

const IconWrap = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(p) => p.bg};
  color: ${(p) => p.color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const CardTitle = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  strong {
    font-size: 16px;
    font-weight: 700;
    color: #0f172a;
  }
  span {
    font-size: 12px;
    color: #64748b;
  }
`

const FeatureList = styled.ul`
  list-style: none;
  margin: 4px 0 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;

  li {
    font-size: 12.5px;
    color: #475569;
    display: flex;
    align-items: center;
    gap: 6px;
    line-height: 1.4;
  }

  li::before {
    content: "•";
    color: ${(p) => p.bullet || "#94a3b8"};
    font-weight: 700;
  }
`

const Footer = styled.div`
  padding: 12px 24px 18px;
  font-size: 11.5px;
  color: #94a3b8;
  text-align: center;
`

export default function ChartTypeSelectModal({ isOpen, onClose, onSelect }) {
  if (!isOpen) return null

  return (
    <Overlay onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <Modal>
        <Header>
          <HeadText>
            <h2>새 차트 만들기</h2>
            <p>어떤 방식으로 사용할 차트인가요? 나중에 변경하기 어려우니 신중하게 선택하세요.</p>
          </HeadText>
          <CloseBtn onClick={onClose}><X size={18} /></CloseBtn>
        </Header>

        <Body>
          {/* 개인 차트 */}
          <Card
            accent="#e5e7eb"
            accentHover="#475569"
            onClick={() => onSelect("personal")}
          >
            <CardHeader>
              <IconWrap bg="#f1f5f9" color="#475569"><User size={26} /></IconWrap>
              <CardTitle>
                <strong>👤 개인 차트</strong>
                <span>나 혼자 쓰는 일정표</span>
              </CardTitle>
            </CardHeader>
            <FeatureList bullet="#94a3b8">
              <li><Lock size={12} color="#94a3b8" /> 본인만 보고 편집</li>
              <li>실시간 동기화 없음 — 가볍게</li>
              <li>혼자 작업 계획·진행 추적에 최적</li>
              <li>공유·초대 기능 비활성</li>
            </FeatureList>
          </Card>

          {/* 팀 차트 */}
          <Card
            accent="#bfdbfe"
            accentHover="#2563eb"
            bg="#f8faff"
            onClick={() => onSelect("team")}
          >
            <CardHeader>
              <IconWrap bg="#dbeafe" color="#2563eb"><Users size={26} /></IconWrap>
              <CardTitle>
                <strong>👥 팀 차트</strong>
                <span>Figma처럼 실시간 협업</span>
              </CardTitle>
            </CardHeader>
            <FeatureList bullet="#2563eb">
              <li><Zap size={12} color="#2563eb" /> 변경사항 실시간 동기화</li>
              <li>다른 멤버 마우스 커서 표시</li>
              <li>접속 중인 멤버 아바타 표시</li>
              <li>공유 링크로 팀원 초대</li>
            </FeatureList>
          </Card>
        </Body>

        <Footer>나중에 차트 메뉴에서 타입 전환은 가능하지만 권장하지 않아요.</Footer>
      </Modal>
    </Overlay>
  )
}
